/** API client — wraps fetch with JWT auth and error handling. */

const BASE = "/api";

function getToken(): string | null {
  return localStorage.getItem("token");
}

export function setToken(token: string) {
  localStorage.setItem("token", token);
}

export function clearToken() {
  localStorage.removeItem("token");
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(`${BASE}${path}`, { ...options, headers });

  if (resp.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body.detail || body.error || detail;
    } catch {}
    throw new Error(detail);
  }
  if (resp.status === 204) return null as T;
  return resp.json();
}

/** Upload a file (multipart). */
async function uploadFile(
  path: string,
  file: File,
  fields: Record<string, string>
): Promise<any> {
  const token = getToken();
  const fd = new FormData();
  fd.append("file", file);
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);

  const resp = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (resp.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body.detail || detail;
    } catch {}
    throw new Error(detail);
  }
  return resp.json();
}

// ── Auth ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  register: (email: string, name: string, password: string) =>
    request("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, name, password }),
    }),
  login: (email: string, password: string) =>
    request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  me: () => request("/auth/me"),
  updateMe: (data: { name?: string; email?: string; photo_url?: string; role?: string }) =>
    request("/auth/me", { method: "PATCH", body: JSON.stringify(data) }),
  changePassword: (current_password: string, new_password: string) =>
    request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),
  deleteAccount: (password: string) =>
    request("/auth/me", { method: "DELETE", body: JSON.stringify({ password }) }),

  // Uploads
  upload: (file: File, specialty: string, report_type?: string) =>
    uploadFile("/uploads/", file, {
      specialty,
      ...(report_type ? { report_type } : {}),
    }),
  listUploads: (specialty?: string, report_type?: string) => {
    const params = new URLSearchParams();
    if (specialty) params.set("specialty", specialty);
    if (report_type) params.set("report_type", report_type);
    return request(`/uploads/?${params}`);
  },
  deleteUpload: (id: number) => request(`/uploads/${id}`, { method: "DELETE" }),
  getUploadMarkdown: (id: number) => request(`/uploads/${id}/markdown`),

  // Data
  getFilters: (specialty?: string, upload_id?: number) => {
    const params = new URLSearchParams();
    if (specialty) params.set("specialty", specialty);
    if (upload_id) params.set("upload_id", String(upload_id));
    return request(`/data/filters?${params}`);
  },
  getTimeseries: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    );
    return request(`/data/timeseries?${qs}`);
  },
  getTable: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    );
    return request(`/data/table?${qs}`);
  },
  getKpis: (specialty_filter?: string) => {
    const qs = new URLSearchParams();
    if (specialty_filter) qs.set("specialty_filter", specialty_filter);
    return request(`/data/kpis?${qs}`);
  },
  getHistory: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    );
    return request(`/data/history?${qs}`);
  },
  getSheetMetrics: () => request("/data/sheet-metrics"),
  rawQuery: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    );
    return request(`/data/query?${qs}`);
  },

  getDirectorBriefHtml: async (specialty_filter = "Cardiology", include_pediatric = true) => {
    const token = getToken();
    const params = new URLSearchParams({
      specialty_filter,
      include_pediatric: String(include_pediatric),
    });
    const resp = await fetch(`${BASE}/data/director-brief-html?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error("Failed to generate director brief");
    return await resp.text();
  },

  // Agent
  chat: (message: string, model?: string) =>
    request("/agent/chat", {
      method: "POST",
      body: JSON.stringify({ message, model }),
    }),
  // Agent — conversations
  getConversations: () => request("/agent/conversations"),
  createConversation: (title: string, model: string) =>
    request("/agent/conversations", { method: "POST", body: JSON.stringify({ title, model }) }),
  getConversationMessages: (id: number) => request(`/agent/conversations/${id}/messages`),
  deleteConversation: (id: number) => request(`/agent/conversations/${id}`, { method: "DELETE" }),
  updateConversation: (id: number, data: Record<string, string>) =>
    request(`/agent/conversations/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  getModels: () => request("/agent/models"),
  chatHistory: () => request("/agent/history"),
  clearChat: () => request("/agent/history", { method: "DELETE" }),
  generatePresentation: (message: string, model?: string, agent_id?: number) =>
    request("/agent/present", {
      method: "POST",
      body: JSON.stringify({ message, model, agent_id }),
    }),

  // Tokens
  tokenSummary: (days = 30) => request(`/tokens/summary?days=${days}`),
  tokensByModel: (days = 30) => request(`/tokens/by-model?days=${days}`),
  tokensDaily: (days = 30) => request(`/tokens/daily?days=${days}`),
  tokensRecent: (limit = 50) => request(`/tokens/recent?limit=${limit}`),

  // Settings
  getSettings: () => request("/settings/"),
  saveOllamaSettings: (settings: any) =>
    request("/settings/ollama-cloud", { method: "POST", body: JSON.stringify(settings) }),
  saveSkills: (skills: any[]) =>
    request("/settings/skills", { method: "POST", body: JSON.stringify(skills) }),
  listSkills: () => request("/settings/skills"),
  createSkill: (skill: any) =>
    request("/settings/skills/create", { method: "POST", body: JSON.stringify(skill) }),
  updateSkill: (name: string, skill: any) =>
    request(`/settings/skills/${encodeURIComponent(name)}`, { method: "PATCH", body: JSON.stringify(skill) }),
  deleteSkill: (name: string) =>
    request(`/settings/skills/${encodeURIComponent(name)}`, { method: "DELETE" }),

  // Agents & connectors
  listAgents: () => request("/agents/"),
  getDefaultAgent: () => request("/agents/default/agent"),
  createAgent: (agent: any) => request("/agents/", { method: "POST", body: JSON.stringify(agent) }),
  updateAgent: (id: number, agent: any) => request(`/agents/${id}`, { method: "PATCH", body: JSON.stringify(agent) }),
  deleteAgent: (id: number) => request(`/agents/${id}`, { method: "DELETE" }),
  listConnectors: () => request("/agents/connectors/list"),
  createConnector: (conn: any) => request("/agents/connectors/", { method: "POST", body: JSON.stringify(conn) }),
  updateConnector: (id: number, conn: any) => request(`/agents/connectors/${id}`, { method: "PATCH", body: JSON.stringify(conn) }),
  deleteConnector: (id: number) => request(`/agents/connectors/${id}`, { method: "DELETE" }),
  getComposioStatus: () => request("/agents/connectors/composio/status"),
  listComposioConnections: () => request("/agents/connectors/composio/connections"),
  listComposioApps: () => request("/agents/connectors/composio/apps"),
  listComposioActions: (toolkit?: string) => request(`/agents/connectors/composio/actions?${toolkit ? `app=${encodeURIComponent(toolkit)}` : ""}`),
  connectComposioApp: (toolkit: string, redirect_url: string) =>
    request("/agents/connectors/composio/connect", { method: "POST", body: JSON.stringify({ toolkit, redirect_url }) }),
  executeComposioAction: (tool_slug: string, params: any) =>
    request("/agents/connectors/composio/execute", { method: "POST", body: JSON.stringify({ tool_slug, params }) }),
};
